import { create } from 'zustand'
import axios from 'axios'
import { ASSISTENT_API_URL } from '@/config/backend'
import { logger } from '@/lib/logger'

interface CallOverview {
  totalCalls: number
  answeredCalls: number
  missedCalls: number
  averageCallDuration: number
  smsCount?: number
  conversationsByState?: { [key: string]: any[] }
  callData: Array<{
    date: string
    calls: number
    answered: number
    missed: number
  }>
}

interface ReportingState {
  dateStart: string | null
  dateEnd: string | null
  callOverview: CallOverview | null
  processing: boolean

  // Actions
  readReportingData: (newDateStart: string, newDateEnd: string) => Promise<void>
  reset: () => void
}

// Helper functions for store interactions
const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Authorization': token,
    'X-AAI-APPLICATIONID': 'r#Oa7e2)tVF!-.,m%z{OCw5S3|*nup'
  }
}

const getCurrentDeviceSerialId = () => {
  return localStorage.getItem('currentDeviceSerialId') || ''
}

const isDeviceSelected = () => {
  return !!getCurrentDeviceSerialId()
}

const showAlertByKey = (key: string) => {
  console.error('Alert:', key)
  // This will be replaced with proper toast notifications
}

export const useReportingStore = create<ReportingState>((set, get) => ({
  dateStart: null,
  dateEnd: null,
  callOverview: null,
  processing: false,

  readReportingData: async (newDateStart: string, newDateEnd: string) => {
    const { dateStart, dateEnd } = get()
    
    logger.reporting('Reading reporting data', {
      newDateStart,
      newDateEnd,
      deviceSelected: isDeviceSelected(),
      deviceId: getCurrentDeviceSerialId()
    })
    
    if ((newDateStart !== dateStart || newDateEnd !== dateEnd) && isDeviceSelected()) {
      const url = `${ASSISTENT_API_URL}device/${getCurrentDeviceSerialId()}/usage/${newDateStart}/${newDateEnd}`

      set({ 
        processing: true,
        dateStart: newDateStart,
        dateEnd: newDateEnd
      })

      try {
        const response = await axios.get(url, {
          headers: getAuthHeaders()
        })

        if (response.data.errorCode !== null) {
          logger.error('API returned error', null, { errorCode: response.data.errorCode })
          showAlertByKey(response.data.errorCode)
        } else {
          logger.reporting('Call overview data loaded successfully')
          set({ callOverview: response.data.resultObject })
        }
        
        set({ processing: false })
      } catch (error: any) {
        logger.error('Failed to fetch reporting data', error, {
          status: error.response?.status
        })
        set({ processing: false })
        throw new Error('UNKNOWN_ERROR')
      }
    } else {
      logger.reporting('Skipping data load - dates unchanged or no device selected')
    }
  },

  reset: () => {
    set({
      processing: false,
      dateStart: null,
      dateEnd: null,
      callOverview: null
    })
  }
}))
